import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Custom hook for audio player functionality
 */
export function useAudioPlayer(audioSrc, isSimulationMode = false, isReviewMode = false) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Handle audio time update - only update progress/time, not play state
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current && audioRef.current.duration) {
      const progress = audioRef.current.duration 
        ? (audioRef.current.currentTime / audioRef.current.duration) * 100 
        : 0;
      setAudioProgress(progress);
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  // Handle audio loaded metadata
  const handleAudioLoaded = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration || 0);
      setAudioLoaded(true);
      
      // Auto-play audio when loaded (only in practice mode, not simulation/review)
      // State will be set by 'play' event listener
      if (!isSimulationMode && !isReviewMode && audioRef.current) {
        audioRef.current.play().catch(err => {
          console.error('Auto-play failed:', err);
          // Don't set isPlaying - let event listeners handle it
        });
      }
    }
  }, [isSimulationMode]);

  // Handle audio ended - this is a fallback, also handled by event listener
  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
    setAudioProgress(0);
    setCurrentTime(0);
  }, []);

  // Handle play state change from audio element events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => {
      setIsPlaying(true);
    };
    const handlePause = () => {
      setIsPlaying(false);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setAudioProgress(0);
      setCurrentTime(0);
    };
    const handleWaiting = () => {
      // Audio is buffering but still playing
      setIsPlaying(true);
    };
    const handleCanPlay = () => {
      // Audio is ready to play
      setAudioLoaded(true);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('canplay', handleCanPlay);
      
      // Pause audio when component unmounts
      audio.pause();
    };
  }, [audioSrc]);

  // Toggle play/pause
  const togglePlay = useCallback(async () => {
    if (!audioRef.current) return;
    
    try {
      if (audioRef.current.paused) {
        // Play the audio - state will be set by 'play' event listener
        await audioRef.current.play();
      } else {
        // Pause the audio - state will be set by 'pause' event listener
        audioRef.current.pause();
      }
    } catch (error) {
      console.error('Error toggling playback:', error);
      // On error, sync state with actual audio state
      if (audioRef.current) {
        setIsPlaying(!audioRef.current.paused);
      }
    }
  }, []);

  // Skip forward
  const skipForward = useCallback(() => {
    if (audioRef.current && audioRef.current.duration) {
      audioRef.current.currentTime = Math.min(
        audioRef.current.currentTime + 5, 
        audioRef.current.duration
      );
    }
  }, []);

  // Skip backward
  const skipBackward = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(audioRef.current.currentTime - 5, 0);
    }
  }, []);

  const seekTo = useCallback((seconds) => {
    if (!audioRef.current || !audioRef.current.duration) return;
    const nextTime = Math.min(Math.max(Number(seconds) || 0, 0), audioRef.current.duration);
    audioRef.current.currentTime = nextTime;
    setCurrentTime(nextTime);
    setAudioProgress((nextTime / audioRef.current.duration) * 100);
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      const newMutedState = !audioRef.current.muted;
      audioRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
    }
  }, []);

  // Reset audio when source changes
  useEffect(() => {
    if (audioRef.current && audioSrc) {
      // Pause and reset if currently playing
      // State will be updated by pause event listener
      if (!audioRef.current.paused) {
        audioRef.current.pause();
      }
      audioRef.current.load();
      setIsPlaying(false);
      setAudioProgress(0);
      setCurrentTime(0);
      setDuration(0);
      setAudioLoaded(false);
    }
  }, [audioSrc]);

  return {
    audioRef,
    isPlaying,
    isMuted,
    audioProgress,
    audioLoaded,
    currentTime,
    duration,
    togglePlay,
    skipForward,
    skipBackward,
    seekTo,
    toggleMute,
    handleTimeUpdate,
    handleAudioLoaded,
    handleAudioEnded,
  };
}
